import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  TbBell,
  TbBellRinging,
  TbExternalLink,
  TbLoader2,
  TbX,
} from "react-icons/tb";

const DEFAULT_API_BASE_URL = "https://new.cajuu.app/api";
const POLLING_INTERVAL_MS = 25000;
const NOTIFICATIONS_LIMIT = 30;

const normalizeApiBaseUrl = (value) => {
  const normalizedValue = String(value || "").trim().replace(/\/+$/, "");

  if (!normalizedValue) {
    return DEFAULT_API_BASE_URL;
  }

  if (normalizedValue.endsWith("/api")) {
    return normalizedValue;
  }

  return `${normalizedValue}/api`;
};

const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);

const getToken = () => {
  return (
    localStorage.getItem("userToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    ""
  );
};

const notificationClient = axios.create({
  baseURL: API_BASE_URL,
});

notificationClient.interceptors.request.use((config) => {
  const token = getToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers["Screen-Name"] = "/Notification";
  config.headers.Accept = "application/json";

  return config;
});

const formatNotificationDate = (value) => {
  if (!value) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
      .format(new Date(value))
      .replace(",", "");
  } catch (error) {
    return "";
  }
};

const extractResponseData = (response) => {
  return response?.data?.data || response?.data || {};
};

const NotificationBell = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingViewed, setIsMarkingViewed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const containerRef = useRef(null);
  const hasLoadedOnceRef = useRef(false);

  const canUseNotifications = useMemo(() => {
    const roleId = Number(user?.user_role_id);
    return roleId === 1 || roleId === 4;
  }, [user?.user_role_id]);

  const fetchNotifications = useCallback(
    async ({ silent = false } = {}) => {
      if (!canUseNotifications || !getToken()) {
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }

        setErrorMessage("");

        const response = await notificationClient.get("/notifications", {
          params: {
            limit: NOTIFICATIONS_LIMIT,
          },
        });

        const data = extractResponseData(response);

        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        setUnviewedCount(Number(data.unviewed_count || 0));
        hasLoadedOnceRef.current = true;
      } catch (error) {
        if (error?.response?.status !== 403) {
          setErrorMessage("Unable to load notifications.");
        }
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [canUseNotifications]
  );

  const markCurrentNotificationsAsViewed = useCallback(
    async (items) => {
      const unviewedIds = items
        .filter((notification) => !notification.viewed_at)
        .map((notification) => notification.id)
        .filter(Boolean);

      if (!canUseNotifications || unviewedIds.length === 0) {
        return;
      }

      try {
        setIsMarkingViewed(true);

        const response = await notificationClient.post("/notifications/mark-viewed", {
          ids: unviewedIds,
        });

        const data = extractResponseData(response);
        const viewedAt = new Date().toISOString();

        setNotifications((currentNotifications) =>
          currentNotifications.map((notification) =>
            unviewedIds.includes(notification.id)
              ? { ...notification, viewed_at: notification.viewed_at || viewedAt }
              : notification
          )
        );

        setUnviewedCount(Number(data.unviewed_count || 0));
      } catch (error) {
        setErrorMessage("Unable to mark notifications as viewed.");
      } finally {
        setIsMarkingViewed(false);
      }
    },
    [canUseNotifications]
  );

  const openNotifications = async () => {
    const nextOpenState = !isOpen;
    setIsOpen(nextOpenState);

    if (!nextOpenState) {
      return;
    }

    let latestNotifications = notifications;

    if (!hasLoadedOnceRef.current) {
      try {
        setIsLoading(true);
        setErrorMessage("");

        const response = await notificationClient.get("/notifications", {
          params: {
            limit: NOTIFICATIONS_LIMIT,
          },
        });

        const data = extractResponseData(response);
        latestNotifications = Array.isArray(data.notifications) ? data.notifications : [];

        setNotifications(latestNotifications);
        setUnviewedCount(Number(data.unviewed_count || 0));
        hasLoadedOnceRef.current = true;
      } catch (error) {
        setErrorMessage("Unable to load notifications.");
      } finally {
        setIsLoading(false);
      }
    }

    await markCurrentNotificationsAsViewed(latestNotifications);
  };

  const dismissNotification = async (event, notificationId) => {
    event.preventDefault();
    event.stopPropagation();

    if (!notificationId) {
      return;
    }

    const notificationBeingRemoved = notifications.find(
      (notification) => notification.id === notificationId
    );

    setNotifications((currentNotifications) =>
      currentNotifications.filter((notification) => notification.id !== notificationId)
    );

    if (notificationBeingRemoved && !notificationBeingRemoved.viewed_at) {
      setUnviewedCount((currentCount) => Math.max(0, currentCount - 1));
    }

    try {
      const response = await notificationClient.delete(`/notifications/${notificationId}`);
      const data = extractResponseData(response);

      if (typeof data.unviewed_count !== "undefined") {
        setUnviewedCount(Number(data.unviewed_count || 0));
      }
    } catch (error) {
      setErrorMessage("Unable to dismiss notification.");
      await fetchNotifications({ silent: true });
    }
  };

  const redirectToNotification = (notification) => {
    if (!notification?.url) {
      return;
    }

    window.location.assign(notification.url);
  };

  useEffect(() => {
    if (!canUseNotifications) {
      return undefined;
    }

    fetchNotifications({ silent: true });

    const intervalId = window.setInterval(() => {
      fetchNotifications({ silent: true });
    }, POLLING_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotifications({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [canUseNotifications, fetchNotifications]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!containerRef.current || containerRef.current.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  if (!canUseNotifications) {
    return null;
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={openNotifications}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-zinc-700 bg-[#25231f] text-white transition hover:border-orange-400 hover:bg-[#312d27] focus:outline-none focus:ring-2 focus:ring-orange-400/70"
        aria-label="Open notifications"
        title="Notifications"
      >
        {unviewedCount > 0 ? <TbBellRinging size={21} /> : <TbBell size={21} />}

        {unviewedCount > 0 && (
          <span className="absolute -right-1 -top-1 flex min-h-[1.15rem] min-w-[1.15rem] items-center justify-center rounded-full bg-orange-500 px-1 text-[0.65rem] font-bold leading-none text-white shadow-md shadow-black/30">
            {unviewedCount > 99 ? "99+" : unviewedCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-zinc-700 bg-[#1f1d1a] shadow-2xl shadow-black/40">
          <div className="flex items-center justify-between border-b border-zinc-700 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-white">Notifications</p>
              <p className="text-xs text-zinc-400">
                {unviewedCount > 0
                  ? `${unviewedCount} pending notification${unviewedCount === 1 ? "" : "s"}`
                  : "No pending notifications"}
              </p>
            </div>

            {(isLoading || isMarkingViewed) && (
              <TbLoader2 className="animate-spin text-orange-400" size={18} />
            )}
          </div>

          {errorMessage && (
            <div className="border-b border-red-900/50 bg-red-950/40 px-4 py-2 text-xs text-red-200">
              {errorMessage}
            </div>
          )}

          <div className="max-h-[28rem] overflow-y-auto p-2">
            {isLoading && notifications.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-zinc-300">
                <TbLoader2 className="animate-spin" size={18} />
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-zinc-400">
                You have no notifications right now.
              </div>
            ) : (
              notifications.map((notification) => {
                const isUnviewed = !notification.viewed_at;

                return (
                  <div
                    key={notification.id}
                    onClick={() => redirectToNotification(notification)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        redirectToNotification(notification);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className={`relative mb-2 w-full cursor-pointer rounded-xl border px-4 py-3 pr-10 text-left transition last:mb-0 focus:outline-none focus:ring-2 focus:ring-orange-400/70 ${
                      isUnviewed
                        ? "border-orange-500/50 bg-orange-500/10 hover:bg-orange-500/15"
                        : "border-zinc-700 bg-[#25231f] hover:border-zinc-600 hover:bg-[#2c2924]"
                    }`}
                  >
                    {isUnviewed && (
                      <span className="absolute left-2 top-3 h-2.5 w-2.5 rounded-full bg-orange-500 shadow-sm shadow-orange-500/50" />
                    )}

                    <button
                      type="button"
                      onClick={(event) => dismissNotification(event, notification.id)}
                      className="absolute right-2 top-2 rounded-full p-1 text-zinc-400 transition hover:bg-zinc-800 hover:text-white"
                      aria-label="Dismiss notification"
                      title="Dismiss"
                    >
                      <TbX size={16} />
                    </button>

                    <div className="pl-2">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-semibold text-white">{notification.title}</p>
                        {notification.url && (
                          <TbExternalLink className="mt-0.5 shrink-0 text-zinc-500" size={15} />
                        )}
                      </div>

                      <p className="mt-1 text-xs leading-5 text-zinc-300">
                        {notification.message}
                      </p>

                      <p className="mt-2 text-[0.68rem] font-medium uppercase tracking-wide text-zinc-500">
                        {formatNotificationDate(notification.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
